<script setup>
import { onMounted, ref, watch, nextTick, computed } from "vue";
import { useAnalyticsStore } from "@/store/useAnalyticsStore";
import Chart from "chart.js/auto";

const chartRef = ref(null);
const store = useAnalyticsStore();
const isLoading = ref(true);
let chartInstance = null;

const props = defineProps({
  provinceId: String,
});

const emit = defineEmits(["percentageUpdate"]);

// Percentage refs for both metrics
const censadoPercentage = ref(0);
const noCensadoPercentage = ref(0);
const verificadoPercentage = ref(0);
const noVerificadoPercentage = ref(0);
const total = ref(0);

// Revisa si los datos están listos
const isDataReady = computed(() => store.movementData && Object.keys(store.movementData).length > 0);

// Formatea los datos para el gráfico
const formatDataForChart = () => {
  const provinceData = store.movementData.censados;
  
  let censadoTotal = 0;
  let noCensadoTotal = 0;
  let verificadoTotal = 0;
  let noVerificadoTotal = 0;

  if (provinceData) {
    censadoTotal = provinceData.censado || 0;
    noCensadoTotal = provinceData.no_censado || 0;
    verificadoTotal = provinceData.verificado || 0;
    noVerificadoTotal = provinceData.no_verificado || 0;
  }

  total.value = store.movementData.total || 0;

  // Calculate percentages
  if (total.value > 0) {
    const formatPercentage = (pct) => {
      if (pct < 0.01) return '<0.01%';
      if (pct < 0.1) return pct.toFixed(3) + '%';
      if (pct > 99.99) return '>99.99%';
      if (pct > 99.9) return pct.toFixed(1) + '%';
      return pct.toFixed(2) + '%';
    };
    
    censadoPercentage.value = formatPercentage((censadoTotal / total.value) * 100);
    noCensadoPercentage.value = formatPercentage((noCensadoTotal / total.value) * 100);
    verificadoPercentage.value = formatPercentage((verificadoTotal / total.value) * 100);
    noVerificadoPercentage.value = formatPercentage((noVerificadoTotal / total.value) * 100);
    
    emit('percentageUpdate', {
      total: total.value,
      censadoPercentage: censadoPercentage.value,
      noCensadoPercentage: noCensadoPercentage.value,
      verificadoPercentage: verificadoPercentage.value,
      noVerificadoPercentage: noVerificadoPercentage.value
    });
  }

  return { censadoTotal, noCensadoTotal, verificadoTotal, noVerificadoTotal };
};

// Render del gráfico
const renderChart = async () => {
  if (!chartRef.value || !isDataReady.value) {
    console.warn("Chart not rendered: No data or canvas missing.");
    isLoading.value = false;
    return;
  }

  await nextTick(); // Asegura que el canvas esté renderizado

  const ctx = chartRef.value.getContext("2d");

  // Destruye la instancia anterior del gráfico
  if (chartInstance) {
    chartInstance.destroy();
  }

  const { censadoTotal, noCensadoTotal, verificadoTotal, noVerificadoTotal } = formatDataForChart();

  chartInstance = new Chart(ctx, {
    type: "bar",
    data: {
      labels: ["Censado", "Verificado"],
      datasets: [
        { 
          label: "Sí", 
          backgroundColor: ["#63edb8", "#63B3ED"], 
          data: [censadoTotal, verificadoTotal], 
          stack: "stack1",
        },
        { 
          label: "No", 
          backgroundColor: "#c3cfdb", 
          data: [noCensadoTotal, noVerificadoTotal], 
          stack: "stack1",
        },
      ],
    },
    options: {
      indexAxis: "x",
      responsive: true,
      maintainAspectRatio: false,
      plugins: { 
        legend: { 
          display: true,
          position: "top",
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            boxHeight: 8,
            padding: 15
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function (tooltipItem) {
              const datasetLabel = tooltipItem.dataset.label || '';
              const value = tooltipItem.raw;
              const category = tooltipItem.label;
              let total = 0;
              
              if (category === "Censado") {
                total = censadoTotal + noCensadoTotal;
              } else if (category === "Verificado") {
                total = verificadoTotal + noVerificadoTotal;
              }
              
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              return `${datasetLabel}: ${value.toLocaleString()} (${percentage}%)`;
            },
          },
        },
      },
      scales: {
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { 
            drawBorder: false, 
            display: true, 
            drawOnChartArea: true, 
            borderDash: [5, 5] 
          },
          ticks: { 
            display: true, 
            padding: 10, 
            color: "#9ca2b7",
            callback: function(value) {
              return value.toLocaleString();
            }
          },
        },
        x: {
          stacked: true,
          grid: { 
            drawBorder: false, 
            display: false, 
            drawOnChartArea: true, 
            drawTicks: true 
          },
          ticks: { 
            display: true, 
            color: "#9ca2b7", 
            padding: 10 
          },
        },
      }
    },
  });

  isLoading.value = false;
};

// Fetch data and render chart
const fetchDataAndRender = async (provinceId) => {
  isLoading.value = true;

  try {
    await store.fetchMovementData(provinceId);
    await renderChart();
  } catch (error) {
    console.error("Error fetching data:", error);
    isLoading.value = false;
  }

  setTimeout(() => {
    isLoading.value = false;
  }, 5000);
};

onMounted(async () => {
  await fetchDataAndRender(props.provinceId);
});

watch(
  () => props.provinceId,
  async (newProvinceId) => {
    if (newProvinceId) {
      await fetchDataAndRender(newProvinceId);
    }
  },
  { immediate: true }
);

watch(
  () => store.movementData,
  async (newData) => {
    if (newData) {
      await renderChart();
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="row flex-column flex-md-row">
    <div class="chart p-0 w-100">
      <canvas
        ref="chartRef"
        class="chart-canvas"
        width="400"
        height="200"
      ></canvas>
    </div>

    <div class="row w-100 justify-content-center mt-3 ms-4">
      <!-- Censado Percentages -->
      <div class="col-6 mb-2" v-if="!isLoading">
        <div class="d-flex ms-6 justify-content-center align-items-center">
          <div class="d-flex">
            <span class="text-sm text-cyan fw-bold me-3">{{ censadoPercentage }}</span>
            <span class="text-sm text-secondary fw-bold">{{ noCensadoPercentage }}</span>
          </div>
        </div>
      </div>
      
      <!-- Verificado Percentages -->
      <div class="col-6 mb-2" v-if="!isLoading">
        <div class="d-flex justify-content-center align-items-center">
          <div class="d-flex">
            <span class="text-sm text-blue fw-bold me-3">{{ verificadoPercentage }}</span>
            <span class="text-sm text-secondary fw-bold">{{ noVerificadoPercentage }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.chart {
  min-height: 220px;
}
.text-blue {
  color: #63B3ED !important;
}
.text-cyan {
  color: #63edb8 !important;
}
</style>